/**
 * "Notify to:" — the private-note tagging field (Kai's reference: Freshservice
 * shows this exactly like Cc, above the note body, not inline @mentions in the
 * text). Same searchable-chip shape as EmailChipInput, but resolves to REAL
 * agents (id, not a free-typed email) — the server validates the same ids.
 */
import { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { apiFetch } from '../../lib/api';
import type { Person } from './api';
import { NO_AUTOFILL } from '../../components/ui/input';

export function AgentChipInput({
  value,
  onChange,
  candidates,
}: {
  value: Person[];
  onChange: (v: Person[]) => void;
  /** When given (the ticket's group members), search is scoped to them —
   *  notifying an agent outside the group would page someone with no context. */
  candidates?: Person[];
}) {
  // URL state on purpose: typeahead buffer inside a chip input — transient,
  // and the chosen agents (not the search text) are what the form holds.
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Person[]>([]);
  const [open, setOpen] = useState(false);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    const q = query.trim().toLowerCase();
    if (candidates) {
      // Scoped to a known small list — filter client-side, no round trip.
      setResults(
        q.length
          ? candidates.filter(
              (p) =>
                !value.some((v) => v.id === p.id) &&
                ((p.name ?? p.email).toLowerCase().includes(q) || p.email.toLowerCase().includes(q)),
            )
          : candidates.filter((p) => !value.some((v) => v.id === p.id)),
      );
      setOpen(true);
      return;
    }
    if (q.length < 2) {
      setResults([]);
      return;
    }
    debounce.current = setTimeout(async () => {
      try {
        const res = await apiFetch<{ entries: Person[] }>(
          `/api/org/people?kind=agent&q=${encodeURIComponent(q)}`,
        );
        setResults(res.entries.filter((p) => !value.some((v) => v.id === p.id)));
        setOpen(true);
      } catch {
        setResults([]);
      }
    }, 250);
    return () => {
      if (debounce.current) clearTimeout(debounce.current);
    };
  }, [query, value, candidates]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  function add(p: Person) {
    onChange([...value, p]);
    setQuery('');
    setResults([]);
    setOpen(false);
  }
  function remove(id: string) {
    onChange(value.filter((v) => v.id !== id));
  }

  return (
    <div ref={rootRef} className="relative">
      <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-amber-500/40 bg-background px-2 py-1.5 focus-within:ring-2 focus-within:ring-amber-500">
        {value.map((p) => (
          <span key={p.id} className="flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-xs">
            {p.name ?? p.email}
            <button
              type="button"
              aria-label={`Remove ${p.name ?? p.email}`}
              onClick={() => remove(p.id)}
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        {/* `role="combobox"` says what this actually IS — a custom widget,
            not a form field. Correct ARIA regardless, and it is also part of
            what stops the browser classifying it as an address field and
            drawing its own autofill list over ours. */}
        <input
          {...NO_AUTOFILL}
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => (candidates ? setOpen(true) : results.length && setOpen(true))}
          placeholder={value.length ? '' : 'Search…'}
          className="min-w-[8rem] flex-1 border-0 bg-transparent p-0.5 text-sm outline-none placeholder:text-muted-foreground"
        />
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-md border bg-popover shadow-md">
          {results.map((p) => (
            <button
              key={p.id}
              type="button"
              className="flex w-full items-center justify-between px-3 py-1.5 text-left text-sm hover:bg-accent"
              onClick={() => add(p)}
            >
              <span className="truncate">{p.name ?? p.email}</span>
              <span className="shrink-0 text-xs text-muted-foreground">{p.name ? p.email : ''}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
